/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalBody, Label, Input } from 'reactstrap';
import ActionButton from '../../../Components/Common/ActionButton';
import {
  FormField,
  FormGrid,
  RequiredNote,
  SelectField,
  TextAreaField,
  TextField,
  email,
  focusFirstError,
  hasErrors,
  minLength,
  pattern,
  required,
  validateForm,
} from '../../../Components/Form';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { api } from '../../../config';
import Swal from 'sweetalert2';
import notify from '../../../utils/notify';
import './UserFormModal.css';

/* ── Icon helpers (Remix Icon class names) ── */
const IC = {
  user: 'ri-user-3-line',
  mail: 'ri-mail-line',
  lock: 'ri-lock-line',
  phone: 'ri-phone-line',
  brief: 'ri-briefcase-2-line',
  home: 'ri-home-4-line',
  shield: 'ri-shield-keyhole-line',
  camera: 'ri-camera-line',
  eye: 'ri-eye-line',
  eyeOff: 'ri-eye-off-line',
  close: 'ri-close-line',
  save: 'ri-check-line',
  person: 'ri-user-add-line',
  id: 'ri-profile-line',
  building: 'ri-building-2-line',
  file: 'ri-file-text-line',
  group: 'ri-group-line',
};

const PASSWORD_CHECKS = [
  { label: 'At least 8 characters', test: p => p.length >= 8 },
  { label: 'One uppercase letter', test: p => /[A-Z]/.test(p) },
  { label: 'One number', test: p => /[0-9]/.test(p) },
  { label: 'One special character', test: p => /[!@#$%^&*]/.test(p) },
];

const SECTIONS = [
  { id: 'personal', label: 'Personal', icon: IC.user },
  { id: 'employment', label: 'Employment', icon: IC.brief },
  { id: 'contact', label: 'Contact', icon: IC.phone },
  { id: 'security', label: 'Security', icon: IC.shield },
];

// Which tab each validated field lives on, so a failed save can jump to it.
const SECTION_OF = {
  firstName: 'personal',
  lastName: 'personal',
  displayName: 'personal',
  email: 'personal',
  userName: 'security',
  password: 'security',
};
const FIELD_ORDER = Object.keys(SECTION_OF);

const buildSchema = isAddMode => ({
  firstName: { label: 'First name', rules: [required()] },
  lastName: { label: 'Last name', rules: [required()] },
  displayName: { label: 'Display name', rules: [required()] },
  email: { label: 'Email', rules: [required(), email()] },
  ...(isAddMode && {
    userName: { label: 'Username', rules: [required()] },
    password: {
      label: 'Password',
      rules: [
        required(),
        minLength(8, 'Password must be at least 8 characters'),
        pattern(/[A-Z]/, 'Password needs an uppercase letter'),
        pattern(/[!@#$]/, 'Password needs a symbol (! @ # $)'),
      ],
    },
  }),
});

const UserFormModal = ({ isOpen, toggle, userData, onSuccess }) => {
  const token = useSelector(state => state.Login?.user?.token);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    userName: '',
    password: '',
    photo: '',
    employeeNo: '',
    jobTitle: '',
    division: '',
    employeeType: '',
    phoneNumber: '',
    address: '',
    roles: [],
  });
  const [roleOptions, setRoleOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [activeSection, setActiveSection] = useState('personal');
  const isAddMode = !userData;

  /* ── Password strength ── */
  const getPasswordStrength = useCallback(() => {
    const p = formData.password;
    if (!p) return { score: 0, label: '', color: '' };
    let score = 0;
    if (p.length >= 8) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[!@#$%^&*]/.test(p)) score++;
    const map = {
      1: { label: 'Weak', color: '#ef4444' },
      2: { label: 'Fair', color: '#f59e0b' },
      3: { label: 'Good', color: '#3b82f6' },
      4: { label: 'Strong', color: '#10b981' },
    };
    return { score, ...map[score] };
  }, [formData.password]);

  const strength = getPasswordStrength();

  /* ── Fetch roles ── */
  const fetchRoles = async () => {
    try {
      const response = await fetch(
        `${api.API_URL}/role?PageNumber=1&PageSize=200`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();
      const items = (result.data?.items || []).map(item => ({
        value: item.name,
        label: item.name,
      }));
      setRoleOptions(items);
      return items;
    } catch (error) {
      console.error('Error fetchRoles:', error);
      return [];
    }
  };

  useEffect(() => {
    if (isOpen) fetchRoles();
  }, [isOpen]);

  useEffect(() => {
    if (!userData) {
      setFormData({
        firstName: '',
        lastName: '',
        displayName: '',
        email: '',
        userName: '',
        password: '',
        photo: '',
        employeeNo: '',
        jobTitle: '',
        division: '',
        employeeType: '',
        phoneNumber: '',
        address: '',
        roles: [],
      });
      setPreview(null);
      setSelectedFile(null);
      setErrors({});
    } else {
      setFormData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        displayName: userData.displayName || '',
        email: userData.email || '',
        userName: userData.userName || '',
        password: '',
        photo: userData.photo || '',
        employeeNo: userData.employeeNo || '',
        jobTitle: userData.jobTitle || '',
        division: userData.division || '',
        employeeType: userData.employeeType || '',
        phoneNumber: userData.phoneNumber || '',
        address: userData.address || '',
        roles: userData.roles || [],
      });
      setPreview(userData.photo || null);
      setSelectedFile(null);
      setErrors({});
    }
    setActiveSection('personal');
  }, [userData, isOpen]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      notify.warning('Please choose an image file (JPG, PNG, etc.).');
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const newErrors = validateForm(formData, buildSchema(isAddMode));
    setErrors(newErrors);
    if (!hasErrors(newErrors)) return true;
    // Fields on other tabs are hidden, so open the tab that holds the first error.
    const first = FIELD_ORDER.find(name => newErrors[name]);
    setActiveSection(SECTION_OF[first]);
    setTimeout(() => focusFirstError(newErrors, FIELD_ORDER), 0);
    return false;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { photo, ...payload } = formData;
      if (isAddMode) {
        payload.userName = formData.userName;
        payload.password = formData.password;
      }

      let targetId = isAddMode ? null : userData.id;

      if (isAddMode) {
        const res = await axios.post(`${api.API_URL}/appuser`, payload, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        targetId = res.data?.data?.id || res.data?.id || res.data;
        notify.success('User added successfully.');
      } else {
        await axios.put(`${api.API_URL}/appuser/${userData.id}`, payload, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        notify.success('User profile updated successfully.');
      }

      if (selectedFile && targetId) {
        const photoFormData = new FormData();
        photoFormData.append('file', selectedFile);
        try {
          await axios.post(
            `${api.API_URL}/appuser/${targetId}/upload-picture`,
            photoFormData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data',
              },
            }
          );
        } catch (uploadError) {
          console.error('Profile picture upload failed', uploadError);
          notify.warning(
            'The user was saved, but the profile picture could not be uploaded. Edit the user to upload it again.'
          );
        }
      }

      onSuccess?.();
      toggle();
    } catch (error) {
      notify.apiError(error, 'save the user');
    } finally {
      setSubmitting(false);
    }
  };

  const sectionHasError = id =>
    FIELD_ORDER.some(name => SECTION_OF[name] === id && errors[name]);

  const renderSectionNav = () => (
    <div className='ufm-section-nav'>
      {SECTIONS.map(s => (
        <button
          key={s.id}
          type='button'
          className={`ufm-section-btn ${activeSection === s.id ? 'active' : ''}`}
          onClick={() => setActiveSection(s.id)}
        >
          <i className={s.icon} />
          <span>{s.label}</span>
          {sectionHasError(s.id) && (
            <span
              className='ufm-section-error'
              role='img'
              aria-label='Has errors'
            />
          )}
        </button>
      ))}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size='xl'
      className='ufm-modal'
      backdrop='static'
    >
      {/* ── Header ── */}
      <div className='ufm-header'>
        <div className='ufm-header-left'>
          <div className='ufm-header-icon'>
            <i className={isAddMode ? IC.person : IC.user} />
          </div>
          <div>
            <h3 className='ufm-title'>
              {isAddMode ? 'Add New User' : 'Edit User Profile'}
            </h3>
            <p className='ufm-subtitle'>
              {isAddMode
                ? 'Fill in the details to create a new user account'
                : 'Update user information and settings'}
            </p>
          </div>
        </div>
        <button
          className='ufm-close-btn'
          onClick={toggle}
          type='button'
          aria-label='Close'
        >
          <i className={IC.close} />
        </button>
      </div>

      <ModalBody className='ufm-body'>
        <div className='ufm-layout'>
          {/* ── Left: Avatar + Section Nav ── */}
          <div className='ufm-sidebar'>
            {/* Avatar */}
            <div className='ufm-avatar-section'>
              <div className='ufm-avatar-wrapper'>
                <div className='ufm-avatar'>
                  {preview ? (
                    <img src={preview} alt='Profile' />
                  ) : (
                    <div className='ufm-avatar-placeholder'>
                      <i className='ri-user-line' />
                    </div>
                  )}
                  <Label
                    htmlFor='profile-img-file-input'
                    className='ufm-avatar-overlay'
                  >
                    <i className={IC.camera} />
                    <span>Change</span>
                  </Label>
                </div>
                <Input
                  type='file'
                  id='profile-img-file-input'
                  className='d-none'
                  accept='image/png, image/jpeg, image/jpg'
                  onChange={handleFileChange}
                />
              </div>
              <p className='ufm-avatar-hint'>JPG or PNG, max 2MB</p>
              {formData.displayName && (
                <p className='ufm-avatar-name'>{formData.displayName}</p>
              )}
              {formData.email && (
                <p className='ufm-avatar-email'>{formData.email}</p>
              )}
            </div>

            {/* Section navigation */}
            {renderSectionNav()}
          </div>

          {/* ── Right: Form fields ── */}
          <div className='ufm-content'>
            <RequiredNote />

            {/* Personal Section */}
            <div
              className={`ufm-section ${activeSection === 'personal' ? 'active' : ''}`}
            >
              <div className='ufm-section-header'>
                <i className={IC.user} />
                <div>
                  <h5>Personal Information</h5>
                  <p>Basic identity and name details</p>
                </div>
              </div>
              <FormGrid columns={2}>
                <TextField
                  label='First name'
                  name='firstName'
                  required
                  placeholder='e.g. Budi'
                  value={formData.firstName}
                  error={errors.firstName}
                  onChange={handleChange}
                />
                <TextField
                  label='Last name'
                  name='lastName'
                  required
                  placeholder='e.g. Santoso'
                  value={formData.lastName}
                  error={errors.lastName}
                  onChange={handleChange}
                />
                <TextField
                  label='Display name'
                  name='displayName'
                  required
                  placeholder='e.g. Budi Santoso'
                  hint='Shown to other users across the app'
                  value={formData.displayName}
                  error={errors.displayName}
                  onChange={handleChange}
                />
                <TextField
                  label='Email'
                  name='email'
                  type='email'
                  required
                  placeholder='name@company.com'
                  value={formData.email}
                  error={errors.email}
                  onChange={handleChange}
                />
              </FormGrid>
            </div>

            {/* Employment Section */}
            <div
              className={`ufm-section ${activeSection === 'employment' ? 'active' : ''}`}
            >
              <div className='ufm-section-header'>
                <i className={IC.brief} />
                <div>
                  <h5>Employment Details</h5>
                  <p>Job-related information and role assignment</p>
                </div>
              </div>
              <FormGrid columns={2}>
                <TextField
                  label='Employee no.'
                  name='employeeNo'
                  placeholder='e.g. EMP-00123'
                  value={formData.employeeNo}
                  onChange={handleChange}
                />
                <TextField
                  label='Job title'
                  name='jobTitle'
                  placeholder='e.g. Procurement Analyst'
                  value={formData.jobTitle}
                  onChange={handleChange}
                />
                <TextField
                  label='Division'
                  name='division'
                  placeholder='e.g. Commercial'
                  value={formData.division}
                  onChange={handleChange}
                />
                <TextField
                  label='Employee type'
                  name='employeeType'
                  placeholder='e.g. Permanent, Contract'
                  value={formData.employeeType}
                  onChange={handleChange}
                />
                <SelectField
                  span='full'
                  multi
                  label='Roles'
                  name='roles'
                  placeholder='Select one or more roles'
                  hint='Roles decide which menus and actions this user can access'
                  options={roleOptions}
                  value={formData.roles}
                  onChange={roles => setFormData(prev => ({ ...prev, roles }))}
                  noOptionsMessage={() => 'No roles available'}
                />
              </FormGrid>
            </div>

            {/* Contact Section */}
            <div
              className={`ufm-section ${activeSection === 'contact' ? 'active' : ''}`}
            >
              <div className='ufm-section-header'>
                <i className={IC.phone} />
                <div>
                  <h5>Contact Information</h5>
                  <p>Phone number and address details</p>
                </div>
              </div>
              <FormGrid columns={2}>
                <TextField
                  label='Phone number'
                  name='phoneNumber'
                  type='tel'
                  placeholder='+62 812 3456 7890'
                  value={formData.phoneNumber}
                  onChange={handleChange}
                />
                <TextAreaField
                  span='full'
                  label='Address'
                  name='address'
                  placeholder='Street, city, postal code'
                  value={formData.address}
                  onChange={handleChange}
                />
              </FormGrid>
            </div>

            {/* Security Section */}
            <div
              className={`ufm-section ${activeSection === 'security' ? 'active' : ''}`}
            >
              <div className='ufm-section-header'>
                <i className={IC.shield} />
                <div>
                  <h5>Security Credentials</h5>
                  <p>
                    {isAddMode
                      ? 'Sign-in details for the new account'
                      : 'Sign-in details cannot be changed here'}
                  </p>
                </div>
              </div>
              <FormGrid columns={2}>
                {isAddMode ? (
                  <>
                    <TextField
                      label='Username'
                      name='userName'
                      required
                      placeholder='Unique username'
                      value={formData.userName}
                      error={errors.userName}
                      onChange={handleChange}
                    />
                    <FormField
                      label='Password'
                      name='password'
                      required
                      error={errors.password}
                      hint='At least 8 characters, one uppercase letter and one symbol'
                    >
                      {({ invalid, required: isRequired, ...control }) => (
                        <>
                          <div className='ufm-password-wrap'>
                            <Input
                              {...control}
                              type={showPassword ? 'text' : 'password'}
                              value={formData.password}
                              onChange={handleChange}
                              invalid={invalid}
                              required={isRequired}
                              autoComplete='new-password'
                            />
                            <button
                              type='button'
                              className='ufm-eye-btn'
                              onClick={() => setShowPassword(!showPassword)}
                              aria-label={
                                showPassword ? 'Hide password' : 'Show password'
                              }
                            >
                              <i
                                className={showPassword ? IC.eyeOff : IC.eye}
                              />
                            </button>
                          </div>
                          {formData.password && (
                            <div className='ufm-strength'>
                              <div className='ufm-strength-bar'>
                                <div
                                  className='ufm-strength-fill'
                                  style={{
                                    width: `${strength.score * 25}%`,
                                    background: strength.color,
                                  }}
                                />
                              </div>
                              <span
                                className='ufm-strength-label'
                                style={{ color: strength.color }}
                              >
                                {strength.label}
                              </span>
                            </div>
                          )}
                          {formData.password && (
                            <ul className='ufm-pw-checklist'>
                              {PASSWORD_CHECKS.map(check => {
                                const ok = check.test(formData.password);
                                return (
                                  <li
                                    key={check.label}
                                    className={ok ? 'pass' : ''}
                                  >
                                    <i
                                      className={
                                        ok ? 'ri-check-line' : 'ri-close-line'
                                      }
                                    />
                                    {check.label}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </>
                      )}
                    </FormField>
                  </>
                ) : (
                  <>
                    <TextField
                      label='Username'
                      name='userName'
                      mode='readonly'
                      value={formData.userName}
                    />
                    <TextField
                      label='Password'
                      name='password'
                      mode='disabled'
                      value='••••••••'
                      hint='Use the password reset feature to change it'
                    />
                  </>
                )}
              </FormGrid>
            </div>
          </div>
        </div>
      </ModalBody>

      {/* ── Footer ── */}
      <div className='ufm-footer'>
        <ActionButton variant='secondary' icon='ri-close-line' onClick={toggle}>
          Cancel
        </ActionButton>
        <ActionButton
          variant='primary'
          icon={IC.save}
          onClick={handleSubmit}
          loading={submitting}
        >
          {submitting
            ? 'Processing...'
            : isAddMode
              ? 'Create User'
              : 'Save Changes'}
        </ActionButton>
      </div>
    </Modal>
  );
};

export default UserFormModal;
